/**
 * AES-256-GCM encryption for tokens to be sent over URL.
 * Uses Web Crypto API for cryptographically secure encryption.
 * The other side must have the same key to decrypt.
 */

// Convert string to ArrayBuffer
const str2ab = (str: string): ArrayBuffer => {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
};

// Convert ArrayBuffer to string
const ab2str = (buffer: ArrayBuffer): string => {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
};

// Convert ArrayBuffer to Base64URL
const ab2base64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Convert Base64URL to ArrayBuffer
const base64url2ab = (base64url: string): ArrayBuffer => {
  // Restore standard Base64
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  while (base64.length % 4) {
    base64 += '=';
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Derive AES key from password using PBKDF2
const deriveKey = async (
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> => {
  const passwordBuffer = str2ab(password);
  const importedKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      // @ts-ignore - TypeScript type definitions for Web Crypto API vary across versions
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    importedKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

/**
 * Encrypts a token using AES-256-GCM
 * @param token - The token to encrypt
 * @param key - The encryption key (password)
 * @returns Base64URL encoded encrypted token with IV and salt
 */
export async function encryptToken(
  token: string,
  key: string,
): Promise<string> {
  if (!token || !key) return token;

  try {
    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes for GCM

    // Derive encryption key
    const cryptoKey = await deriveKey(key, salt);

    // Encrypt the token
    const encodedToken = str2ab(token);
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      cryptoKey,
      encodedToken,
    );

    // Combine salt + IV + encrypted data
    const combined = new Uint8Array(
      salt.length + iv.length + encryptedBuffer.byteLength,
    );
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encryptedBuffer), salt.length + iv.length);

    // Return as Base64URL
    return ab2base64url(combined.buffer);
  } catch (error) {
    console.error('Encryption failed:', error);
    return token; // Fallback to original token if encryption fails
  }
}

/**
 * Decrypts a token encrypted with encryptToken
 * @param encryptedToken - The encrypted token (Base64URL)
 * @param key - The encryption key (password)
 * @returns Decrypted token string
 */
export async function decryptToken(
  encryptedToken: string,
  key: string,
): Promise<string> {
  if (!encryptedToken || !key) return encryptedToken;

  try {
    // Decode from Base64URL
    const combined = new Uint8Array(base64url2ab(encryptedToken));

    // Extract salt, IV, and encrypted data
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encryptedData = combined.slice(28);

    // Derive decryption key
    const cryptoKey = await deriveKey(key, salt);

    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      cryptoKey,
      encryptedData,
    );

    return ab2str(decryptedBuffer);
  } catch (error) {
    console.error('Decryption failed:', error);
    return encryptedToken; // Return original if decryption fails
  }
}

/**
 * Encrypts the token/accessToken parameter in a URL if it points to the meet URL.
 * @param url - The URL to process
 * @param key - The encryption key
 * @returns URL with encrypted token
 */
export async function encryptUrlToken(
  url: string,
  key: string,
): Promise<string> {
  if (!url || !url.includes('meet.antoree.com')) return url;

  try {
    const urlObj = new URL(url);
    const token =
      urlObj.searchParams.get('token') ||
      urlObj.searchParams.get('accessToken');

    if (token) {
      const encrypted = await encryptToken(token, key);
      if (urlObj.searchParams.has('token')) {
        urlObj.searchParams.set('token', encrypted);
      } else {
        urlObj.searchParams.set('accessToken', encrypted);
      }
      return urlObj.toString();
    }
  } catch (e) {
    console.error('Failed to parse URL for token encryption:', e);
  }
  return url;
}

/**
 * Synchronous version using XOR cipher (for backward compatibility)
 * Use this only if you need synchronous encryption
 * @deprecated Use encryptToken (async) for better security
 */
export function encryptTokenSync(token: string, key: string): string {
  if (!token || !key) return token;
  const result = [];
  for (let i = 0; i < token.length; i++) {
    result.push(
      String.fromCharCode(token.charCodeAt(i) ^ key.charCodeAt(i % key.length)),
    );
  }
  return btoa(result.join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Synchronous decryption for XOR cipher
 * @deprecated Use decryptToken (async) for better security
 */
export function decryptTokenSync(encryptedToken: string, key: string): string {
  if (!encryptedToken || !key) return encryptedToken;
  try {
    // Restore standard Base64
    let base64 = encryptedToken.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    const decoded = atob(base64);
    const result = [];
    for (let i = 0; i < decoded.length; i++) {
      result.push(
        String.fromCharCode(
          decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length),
        ),
      );
    }
    return result.join('');
  } catch (error) {
    console.error('Decryption failed:', error);
    return encryptedToken;
  }
}

export const ENCRYPTION_KEY = 'Zp9!vQ$r2L#mK8*nXw5@tB4&uY7^jA1%';
